<template>
    <div
        style="position: relative;"
        :class="[
            { 'required-field-placeholder-hint': required && !inputValue },
            inputStyle === 'tailwind' ? 'input-style-tailwind' : '',
        ]"
    >
        <v-menu
            v-model="showSuggestions"
            :open-on-click="false"
            :close-on-content-click="false"
            disable-keys
            offset-y
            max-height="300"
            min-width="0"
            max-width="calc(100vw - 24px)"
            content-class="company-suggestions-menu"
        >
            <template #activator="{ attrs }">
                <div v-bind="attrs">
                    <template v-if="inputStyle === 'tailwind'">
                        <div class="space-y-1.5">
                            <label v-if="label" class="text-xs font-semibold text-slate-500 uppercase tracking-wider">{{ label }}</label>
                            <div class="relative">
                                <input
                                    ref="textField"
                                    type="text"
                                    :value="inputValue"
                                    :disabled="disabled"
                                    :placeholder="displayPlaceholder"
                                    :autofocus="autofocus"
                                    :class="[twInputClass, errorMessages && errorMessages.length ? 'border-red-500' : '']"
                                    @input="handleInput($event.target.value)"
                                    @focus="onFocus"
                                    @blur="onBlur"
                                    @keydown="onKeydown"
                                />
                                <v-progress-circular
                                    v-if="loading"
                                    indeterminate
                                    size="20"
                                    width="2"
                                    class="absolute right-3 top-1/2 transform -translate-y-1/2"
                                />
                            </div>
                            <p v-if="errorMessages && errorMessages.length" class="text-xs text-red-600">{{ errorMessages[0] }}</p>
                        </div>
                    </template>
                    <v-text-field
                        v-else
                        ref="textField"
                        v-model="inputValue"
                        :disabled="disabled"
                        :error-messages="errorMessages"
                        :label="label"
                        :placeholder="displayPlaceholder"
                        :persistent-placeholder="required"
                        :dense="dense"
                        :outlined="outlined"
                        :clearable="clearable"
                        :loading="loading"
                        hide-details="auto"
                        @input="handleInput"
                        @focus="onFocus"
                        @blur="onBlur"
                        @keydown="onKeydown"
                        :autofocus="autofocus"
                    />

                </div>
            </template>
            <v-list v-if="suggestions.length" dense class="suggestions-dropdown" @mousedown.prevent>
                <v-list-item
                    v-for="(suggestion, index) in suggestions"
                    :key="suggestion.siren || suggestion.name || index"
                    :class="{ 'v-list-item--active': index === selectedIndex }"
                    @click="selectSuggestion(suggestion)"
                    @mouseenter="selectedIndex = index"
                    style="cursor: pointer; padding: 12px 16px;"
                >
                    <v-list-item-content>
                        <v-list-item-title style="font-weight: 500; margin-bottom: 4px;">
                            {{ suggestion.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle style="font-size: 0.8rem; color: #666; line-height: 1.4;">
                            <div v-if="suggestion.siren" style="margin-bottom: 2px;">
                                <strong>SIREN:</strong> {{ suggestion.siren }}
                            </div>
                            <div v-if="suggestion.line1 || suggestion.line2 || suggestion.city || suggestion.postal_code || suggestion.country" style="margin-bottom: 2px;">
                                <strong>Adresse:</strong>
                                <span v-if="suggestion.line1">{{ suggestion.line1 }}</span>
                                <span v-if="suggestion.line2">, {{ suggestion.line2 }}</span>
                                <span v-if="(suggestion.line1 || suggestion.line2) && (suggestion.city || suggestion.postal_code)">, </span>
                                <span v-if="suggestion.city">{{ suggestion.city }}</span>
                                <span v-if="suggestion.postal_code"> {{ suggestion.postal_code }}</span>
                                <span v-if="suggestion.country && (suggestion.city || suggestion.postal_code)">, </span>
                                <span v-if="suggestion.country">{{ suggestion.country }}</span>
                            </div>
                            <div v-if="suggestion.vat_number" style="margin-bottom: 2px;">
                                <strong>TVA:</strong> {{ suggestion.vat_number }}
                            </div>
                        </v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Premium gate dialog when company completion is not available -->
        <v-dialog
            v-model="showPremiumGateDialog"
            max-width="600"
            persistent
            content-class="premium-gate-dialog"
        >
            <v-card>
                <PremiumFeatureGateCompanyCompletion :inline="false" @input="showPremiumGateDialog = $event" />
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import GlobalMixins from "@/mixins/global";
import PremiumFeatureGateCompanyCompletion from "@/components/common/premium-feature-gate-company-completion";
import { requiredPlaceholder } from "@/utils/required-field";

export default {
    name: 'CompanySearchAutocomplete',
    mixins: [GlobalMixins],
    components: {
        PremiumFeatureGateCompanyCompletion,
    },
    props: {
        value: {
            type: String,
            default: ''
        },
        errorMessages: {
            type: Array,
            default: () => []
        },
        label: {
            type: String,
            default: 'Company name or SIREN'
        },
        placeholder: {
            type: String,
            default: 'Start typing a company name or SIREN...'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: true
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: false
        },
        /** 'vuetify' (default) or 'tailwind' – controls input visual style */
        inputStyle: {
            type: String,
            default: 'vuetify',
            validator: (v) => ['vuetify', 'tailwind'].includes(v),
        },
        required: {
            type: Boolean,
            default: false,
        },
        /** Allow Pappers auto-completion without Premium (own entity / onboarding). */
        allowCompanyCompletion: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            inputValue: this.value || '',
            suggestions: [],
            loading: false,
            debounceTimer: null,
            showSuggestions: false,
            selectedIndex: -1,
            isFocused: false,
            showPremiumGateDialog: false,
        };
    },
    computed: {
        ...mapGetters("session", ["current_entity"]),
        canUseCompanyCompletion() {
            return (
                this.allowCompanyCompletion ||
                this.current_entity?.subscription === "premium"
            );
        },
        pappersContext() {
            return this.allowCompanyCompletion ? "own_entity" : undefined;
        },
        displayPlaceholder() {
            if (!this.required) {
                return this.placeholder;
            }
            return requiredPlaceholder(this.placeholder, this.$translate("requiredSuffix"));
        },
        twInputClass() {
            return 'w-full bg-slate-50 border border-slate-200 dark:border-slate-700 rounded text-sm px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
        },
    },
    methods: {
        handleInput(value) {
            this.inputValue = value;
            this.$emit('input', value);
            
            if (value && value.length >= 3) {
                this.performSearch(value);
            } else {
                this.suggestions = [];
                this.showSuggestions = false;
            }
        },
        onFocus() {
            this.isFocused = true;
            if (this.suggestions.length > 0) {
                this.showSuggestions = true;
            }
        },
        onBlur() {
            this.isFocused = false;
            setTimeout(() => {
                this.showSuggestions = false;
            }, 200);
        },
        onKeydown(event) {
            if (!this.showSuggestions || this.suggestions.length === 0) return;
            
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.selectedIndex >= 0) {
                        this.selectSuggestion(this.suggestions[this.selectedIndex]);
                    }
                    break;
                case 'Escape':
                    this.showSuggestions = false;
                    this.selectedIndex = -1;
                    break;
            }
        },
        async selectSuggestion(suggestion) {
            if (!this.canUseCompanyCompletion) {
                this.showPremiumGateDialog = true;
                return;
            }

            this.inputValue = suggestion.name;
            this.$emit('input', suggestion.name);
            this.showSuggestions = false;
            this.selectedIndex = -1;

            if (suggestion.siren) {
                try {
                    const response = await axios.get('/api/company/info/', {
                        params: {
                            query: suggestion.siren,
                            ...(this.pappersContext ? { context: this.pappersContext } : {}),
                        }
                    });

                    if (response.data) {
                        this.$emit('company-selected', response.data);
                    } else {
                        this.$emit('company-selected', suggestion);
                    }
                } catch (error) {
                    console.error('Error fetching company details:', error);
                    this.$emit('company-selected', suggestion);
                }
            } else {
                this.$emit('company-selected', suggestion);
            }
        },
        performSearch(query) {
            if (!query || query.length < 3) {
                this.suggestions = [];
                this.showSuggestions = false;
                return;
            }

            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = setTimeout(() => {
                this.searchCompanies(query);
            }, 1000);
        },
        async searchCompanies(query) {
            if (!query || query.length < 3) {
                this.suggestions = [];
                this.showSuggestions = false;
                return;
            }

            this.loading = true;

            try {
                const response = await axios.get('/api/company/search/', {
                    params: {
                        query: query,
                        limit: 10,
                        ...(this.pappersContext ? { context: this.pappersContext } : {}),
                    }
                });

                this.loading = false;
                
                if (response.data && response.data.results) {
                    this.suggestions = response.data.results;
                    
                    if (this.isFocused) {
                        this.showSuggestions = true;
                    }
                } else {
                    this.suggestions = [];
                    this.showSuggestions = false;
                }
            } catch (error) {
                this.loading = false;
                console.error('Error searching companies:', error);
                this.suggestions = [];
                this.showSuggestions = false;
            }
        },
        
        triggerSearch(query) {
            if (query && query.length >= 3) {
                this.inputValue = query;
                this.isFocused = true;
                this.performSearch(query);
            }
        }
    },
    watch: {
        value(newValue) {
            if (newValue !== this.inputValue) {
                this.inputValue = newValue;
            }
        }
    },
    i18n: {
        messages: {
            en: {
                close: 'Close',
                requiredSuffix: 'Required',
                company_search: {
                    placeholder: 'Start typing a company name or SIREN...',
                    no_results: 'No companies found',
                    search_error: 'Error searching companies'
                }
            },
            fr: {
                close: 'Fermer',
                requiredSuffix: 'Requis',
                company_search: {
                    placeholder: 'Commencez à taper un nom de société ou un SIREN...',
                    no_results: 'Aucune société trouvée',
                    search_error: 'Erreur lors de la recherche de sociétés'
                }
            }
        }
    }
};
</script>

<style scoped>
.v-autocomplete .v-list-item__title {
    font-size: 0.875rem;
    line-height: 1.25rem;
}

.v-autocomplete .v-list-item__subtitle {
    font-size: 0.75rem;
    opacity: 0.7;
}

.suggestions-dropdown {
    background: white;
}

.suggestions-dropdown .v-list-item {
    border-bottom: 1px solid #f0f0f0;
}

.suggestions-dropdown .v-list-item:last-child {
    border-bottom: none;
}

.suggestions-dropdown .v-list-item:hover {
    background-color: #f5f5f5;
}

.suggestions-dropdown .v-list-item--active {
    background-color: #e3f2fd;
}

.input-style-tailwind input {
    border-width: 1px !important;
    border-style: solid !important;
    border-color: #e2e8f0 !important;
}
.input-style-tailwind input:focus {
    border-color: #3b82f6 !important;
}
</style>
